/**
 * After SSE text tokens finish, the buffer is the model's JSON (optionally in a ```json fence).
 * Mirrors the edge function's parseAssistantAction fence + JSON step so we show `text`, not raw JSON.
 */
export function parseAssistantStreamAccumulated(fullText) {
  const raw = fullText == null ? '' : String(fullText);
  const trimmed = raw.trim();
  if (!trimmed) return { text: '', action: null };

  const fence = trimmed.match(/^```(?:json)?\s*([\s\S]*?)```/m);
  const source = fence ? fence[1].trim() : trimmed;

  // Try clean parse first: slice from first { to last }.
  const firstBrace = source.indexOf('{');
  const lastBrace = source.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    try {
      const parsed = JSON.parse(source.slice(firstBrace, lastBrace + 1));
      const text = sanitizePlainText(typeof parsed.text === 'string' ? parsed.text : '');
      const action =
        parsed.action && typeof parsed.action === 'object' ? parsed.action : null;
      if (text || action) return { text, action };
    } catch {
      // fall through to rescue path
    }
  }

  // Rescue path: truncated / malformed JSON (e.g. response hit max_tokens
  // mid-string). Extract the "text" field with a regex so the user still sees
  // useful content instead of an empty bubble or a generic error banner.
  const textMatch = source.match(/"text"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (textMatch) {
    try {
      const rescued = JSON.parse(`"${textMatch[1]}"`);
      if (rescued) return { text: sanitizePlainText(rescued), action: null };
    } catch {
      // ignore
    }
  }

  // Total bust. If there's no `{` anywhere the model spoke plain prose — show it,
  // but only if it doesn't itself look like leaked JSON (defensive — users must never
  // see `"text":` / `"action":` / literal escape sequences in a chat bubble).
  if (firstBrace === -1 && !looksLikeLeakedJson(raw)) {
    return { text: sanitizePlainText(raw), action: null };
  }
  return { text: '', action: null };
}

/**
 * Strip programming-identifier-shaped tokens from chat-bubble text.
 *
 * Claude occasionally leaks schema field names (`customer_list`, `label_key`)
 * or i18n key fragments (`te_list`, `chip.whatsNext`) into the `text` field
 * even when the system prompt forbids it. The model is non-deterministic —
 * the prompt is best-effort, this is the guarantee. Bubble must always be
 * plain prose.
 *
 * Conservative: only strips tokens with internal underscores or i18n-style
 * dot paths. Spanish/English prose has neither, so false positives are
 * unlikely. Customer names with underscores would be pre-fill data, never
 * in the user-visible text bubble (the system prompt forbids restating
 * names in prose anyway).
 */
function sanitizePlainText(s) {
  if (typeof s !== 'string' || !s) return s || '';
  let out = s;
  // Strip trailing " — programming_token" / " - foo.bar.baz" patterns.
  out = out.replace(/\s*[—–-]\s*[a-z][a-z0-9]*(?:[_.][a-z0-9]+)+\s*$/i, '');
  // Strip standalone underscored or dotted tokens anywhere in the line.
  out = out.replace(/\s\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/gi, ' ');
  out = out.replace(/\s\b[a-z][a-z0-9]*(?:\.[a-z0-9]+){2,}\b/gi, ' ');
  // Collapse double spaces created by the strip.
  out = out.replace(/  +/g, ' ').trim();
  return out;
}

function looksLikeLeakedJson(s) {
  if (!s) return false;
  // Any of these patterns means we'd be showing the JSON shell, not the message.
  if (/"text"\s*:/.test(s)) return true;
  if (/"action"\s*:/.test(s)) return true;
  // High density of literal `\n` two-character escape sequences => JSON-encoded source.
  const escapedNewlines = (s.match(/\\n/g) || []).length;
  if (escapedNewlines >= 3) return true;
  return false;
}
